const OBJECT_TYPE_MAP = {
  employee: "employee",
  position: "position",
  role: "role",
  responsibility: "responsibility",
  "responsibility-to-role": "responsibility-to-role",
  responsibility_to_role: "responsibility-to-role",
};

/**
 * Normalises a raw object-type string from the API to a display wire value.
 * Returns the mapped value when recognised, otherwise returns the raw string unchanged.
 * @param {string} raw
 * @returns {string}
 */
export function formatObjectType(raw) {
  return OBJECT_TYPE_MAP[String(raw).toLowerCase()] || raw;
}

/**
 * Returns a human-readable date group label for a given Date:
 * "Today", "Yesterday", or a formatted locale string (e.g. "March 12, 2026").
 * @param {Date} date
 * @returns {string}
 */
export function formatDateLabel(date) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Returns a compact relative-time string (e.g. "2h ago", "3d ago").
 * @param {Date} date
 * @returns {string}
 */
export function getRelativeTime(date) {
  const diff = Date.now() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return `${seconds}s ago`;
}

/**
 * Canonical detail routes for each covered object type (Rule 13). Used only to
 * build the subject/counterpart link; entries whose type has no route (the
 * responsibility_to_role link entity) render as plain text — never a dead link.
 * Route patterns mirror the app's own `$router.push` call sites.
 */
const OBJECT_DETAIL_ROUTE = {
  employee: (id) => `/organization/employees/${id}`,
  position: (id) => `/organization/positions/position/${id}`,
  role: (id) => `/organization/roles/${id}`,
  responsibility: (id) => `/organization/responsibilities/${id}`,
};

/**
 * Builds an internal detail link for an object type + id, or "" when no route
 * is known / the id is absent. Plain text rendering is the safe fallback.
 * @param {string} type
 * @param {string|number} id
 * @returns {string}
 */
export function buildObjectDetailLink(type, id) {
  const builder = OBJECT_DETAIL_ROUTE[type];
  if (!builder || id == null || id === "") return "";
  return builder(id);
}

/**
 * Maps a T3 activity-feed card onto ActivityStreamCard props (Rule 3).
 * Descriptor, verb, and counterpart are taken verbatim — never recomputed.
 *
 * MDP-606 (ER-11800) structured fields are passed through verbatim (Rule 4);
 * the backend contract for them lands with ER-11798, so until then they are
 * absent and the card simply renders the legacy shape. Links are only emitted
 * when the feed endpoint has positively resolved subject_exists /
 * counterpart_exists (Rule 13) — an unknown existence degrades to plain text,
 * which is the "never a dead link" invariant.
 * @param {object} card
 * @returns {object}
 */
export function transformActivityCard(card = {}) {
  const datetime = card.datetime || null;

  const subjectType = formatObjectType(card.subject_type || card.object_type || "");
  const subjectId = card.subject_id != null ? card.subject_id : null;
  const counterpartType = formatObjectType(card.counterpart_type || "");
  const counterpartId = card.counterpart_id != null ? card.counterpart_id : null;

  const subject_href =
    card.subject_exists === true ? buildObjectDetailLink(subjectType, subjectId) : "";
  const counterpart_href =
    card.counterpart_exists === true ? buildObjectDetailLink(counterpartType, counterpartId) : "";

  return {
    id: card.id,
    actor_avatar: card.actor_avatar || "",
    actor_name: card.actor_name || "",
    object_type: formatObjectType(card.object_type || ""),
    object_descriptor: card.object_descriptor || "",
    verb: card.verb || "",
    counterpart: card.counterpart || "",
    datetime,
    is_system_entry: Boolean(card.is_system_entry),
    // Rule 12: legacy == "no action_code at all". Derive from action_code rather
    // than trusting the API's is_legacy flag, which the legacy fallback renderer
    // set to true for unregistered-but-current action codes and so showed the
    // Legacy pill on fresh posts (ER-11568).
    is_legacy: !card.action_code,
    action_code: card.action_code || "",
    subject_link: card.subject_link || "",

    // ── MDP-606 structured fields (Rule 4) — verbatim pass-through ────────
    subject_name: card.subject_name || "",
    subject_id: subjectId,
    subject_label: card.subject_label || "",
    counterpart_name: card.counterpart_name || "",
    detail_text: card.detail_text || "",
    // Rule 14: is_system = true is the sole discriminator for the System
    // identity on the new card.
    is_system: Boolean(card.is_system),
    subject_href,
    counterpart_href,

    createdOnMs: datetime ? new Date(datetime).getTime() : 0,
  };
}

/**
 * Transforms a raw array of activity-feed cards into UI-ready activity items.
 * @param {Array} cards
 * @returns {Array}
 */
export function transformActivityData(cards) {
  return (cards || []).map(transformActivityCard);
}
